import { getApi } from '../api';
import { getDeviceAndUserStore, getAccountStore } from '../storage/stores';
import { captureException } from '@sentry/browser';
import { isSupported } from 'firebase/messaging/sw';
import { obtainFcmToken } from './obtainFcmToken';
import { ensurePushSubscription } from './messaging';
import { PUSH_TAG } from './constants';

/**
 * Ensure FCM token is registered and synced to backend
 *
 * Combines push subscription setup, token acquisition and backend sync.
 * Call on first session init when notifications permission is granted.
 *
 * Calls ensurePushSubscription() before obtainFcmToken() to ensure our subscription
 * config (userVisibleOnly: false) is used instead of Firebase's default.
 *
 * Only reports unexpected errors to Sentry (bugs, config issues).
 * Expected errors (permissions, IndexedDB unavailable, network issues) are logged but not reported.
 * Extension continues to function if FCM setup fails.
 */
export const ensureFcmTokenRegistered = async (): Promise<void> => {
  try {
    // Check Firebase Messaging support before attempting anything
    const supported = await isSupported();
    if (!supported) {
      console.warn(
        `${PUSH_TAG} Push Messaging not supported in this context (private browsing or incompatible browser)`
      );
      return;
    }

    await ensurePushSubscription();
    await obtainFcmToken();
    await syncFcmTokenToBackend();
  } catch (error) {
    const err = error as Error;
    console.error(`${PUSH_TAG} Token registration failed:`, err.message);
    captureException(err);
  }
};

/**
 * Sync FCM token to backend
 *
 * Sends stored token to backend API. Idempotent: skips if token already sent.
 * Updates fcm.lastSentToken on success.
 *
 * @throws Error when stores are not initialized or backend call fails
 */
export const syncFcmTokenToBackend = async (): Promise<void> => {
  const deviceUserData = getDeviceAndUserStore().get();

  if (!deviceUserData?.fcm?.pushToken) {
    throw new Error('No FCM token found in storage');
  }

  const { pushToken: token, lastSentToken } = deviceUserData.fcm;

  if (lastSentToken === token) {
    return;
  }

  const accountData = getAccountStore().get();
  if (!accountData) {
    throw new Error('Account store not initialized');
  }

  await getApi().pushNotificationToken.post(
    {
      platform: 'FCM_Chrome',
      registration_id: token,
    } as const,
    {},
    {
      accountId: accountData.accountId,
      deviceUid: deviceUserData.device,
      userUid: deviceUserData.user,
    }
  );

  await getDeviceAndUserStore().set({
    fcm: {
      ...deviceUserData.fcm,
      lastSentToken: token,
    },
  });
};
